import { EllipsisOutlined, UpOutlined } from '@ant-design/icons'
import { Alarm, useAlarmList, useCheckAlarm } from '@services/alarm'
import { useConvertData, useDevice } from '@services/device'
import { momentToString } from '@shared/innmaxLib/utils/webHelper'
import { MapActiveMarkerState, useMap } from '@shared/innmaxMap/map/MapProvider'
import { Modal } from '@shared/innmaxUI'
import { Dropdown } from '@shared/innmaxUI/Dropdown'
import { Skeleton, Space, Spin } from 'antd'
import cx from 'classnames'
import moment from 'moment'
import { T, always, cond, equals } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { ALARM_HISTORY } from 'src/routes'
import { css } from 'styled-components'
// import useDashboard from '../DashboardProvider'

type FilterType = 'Today' | 'Checked'

const query = {
  startAt: moment().subtract(24, 'hours').valueOf(),
  endAt: moment().valueOf(),
  pageNum: 0,
  pageSize: 9999,
}

const AlarmList = React.memo(
  (props: {
    onOpen?: (open: boolean) => void
    lastUpdated?: moment.Moment
  }) => {
    const { onOpen, lastUpdated } = props

    // const { highlightAlarmIds, setHighlightAlarmIds, setSelectedAlarm } =
    //   useDashboard()

    const history = useHistory()
    const { t } = useTranslation()

    const [open, setOpen] = React.useState(false)
    const [filterType, setFilterType] = React.useState<FilterType | undefined>(
      undefined
    )

    const {
      dataSource: alarmDataSource,
      refetch: refetchAlarm,
      isLoading,
    } = useAlarmList(query)

    React.useEffect(() => {
      refetchAlarm()
    }, [lastUpdated])

    React.useEffect(() => {
      //   if (!open && highlightAlarmIds.length > 0) {
      //     setHighlightAlarmIds([])
      //   }
      onOpen && onOpen(open)
    }, [open])

    const handleFilterClick = (type: FilterType) => {
      if (filterType === type) {
        setFilterType(undefined)
        return
      }
      setFilterType(type)
    }

    // const handleVsDevices = (d: Alarm) => {
    //   setSelectedAlarm({ ...d, cache: new Date().getTime() } as any)
    // }

    // React.useEffect(() => {
    //   if (highlightAlarmIds.length === 0) {
    //     return
    //   }
    //   setOpen(true)
    // }, [highlightAlarmIds])

    const { handleCheck, isCheckNow } = useCheckAlarm()

    const handleCheckAllAlarms = () => {
      handleCheck(
        alarmDataSource.map((alarm: Alarm) => ({
          id: alarm.id,
          check: true,
        })),
        {
          onSuccess: refetchAlarm,
        }
      )
    }

    const uncheckedDataSource = React.useMemo(
      () => alarmDataSource.filter((alarm: Alarm) => !alarm.checked),
      [alarmDataSource]
    )

    const todayDataSource = React.useMemo(
      () =>
        alarmDataSource.filter((alarm: Alarm) =>
          moment(alarm.createAt).isSame(moment(), 'day')
        ),
      [alarmDataSource]
    )

    return (
      <div
        css={css`
          grid-area: alarm-list;
          height: ${`${open ? '420px' : 'max-content'}`};
          background-color: rgba(27, 29, 29, ${open ? 1 : 0.9});
          width: 100%;
          transition: height 0.4s, background-color 0.6s;
          pointer-events: auto;
          padding-bottom: 10px;
        `}>
        <div
          css={css`
            padding: 0px 15px;
            font-size: 14px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: #fff;
            margin: 5px 0;
          `}>
          <label>{t('dashboard:24_hours_alarm_events')}</label>
          <Space size="middle">
            <div
              onClick={() => setOpen(_open => !_open)}
              css={css`
                cursor: pointer;
                transform: rotateZ(${`${open ? 180 : 0}deg`});
                transition: transform 0.6s;
              `}>
              <UpOutlined />
            </div>
            <Dropdown
              menu={[
                {
                  key: 'allAlarm',
                  label: t('dashboard:confirm_all_pending_alarms'),
                  disabled: uncheckedDataSource.length === 0,
                  onClick: () => {
                    uncheckedDataSource.length > 0 &&
                      Modal.confirm({
                        content: t('dashboard:confirm_all_pending_alarms'),
                        onOk: handleCheckAllAlarms,
                      })
                  },
                  style: { textAlign: 'center' },
                },
                {
                  key: 'historyAlarm',
                  label: t('dashboard:view_history_records'),
                  onClick: () => {
                    history.push(ALARM_HISTORY)
                  },
                  style: { textAlign: 'center' },
                },
              ]}>
              <EllipsisOutlined />
            </Dropdown>
          </Space>
        </div>
        <div
          css={css`
            display: flex;
            height: calc(100% - 32px);
            padding-left: 5px;
          `}>
          <Space direction="vertical">
            <FilterButton
              actived={filterType === 'Today'}
              type="Today"
              count={todayDataSource.length}
              onClick={handleFilterClick}
            />
            <FilterButton
              actived={filterType === 'Checked'}
              type="Checked"
              count={uncheckedDataSource.length}
              onClick={handleFilterClick}
            />
          </Space>
          <Skeleton
            loading={isLoading || isCheckNow}
            active
            title={false}
            paragraph={{
              rows: 3,
              width: ['100%', '100%', '100%'],
            }}>
            <div
              css={css`
                flex: 1;
                margin-left: 10px;
                overflow: auto;
              `}>
              {(filterType === 'Today' ? todayDataSource : uncheckedDataSource)
                .slice(0, open ? undefined : 3)
                .map((alarm: Alarm) => (
                  <AlarmRow
                    key={alarm.id}
                    alarm={alarm}
                    //   highlight={highlightAlarmIds.includes(alarm.id)}
                    onSuccess={refetchAlarm}
                    //   handleVsDevices={handleVsDevices}
                    highlight={false}
                    handleVsDevices={() => {}}
                  />
                ))}
            </div>
          </Skeleton>
        </div>
      </div>
    )
  }
)

export default AlarmList

const FilterButton = (props: {
  type: FilterType
  count: number
  onClick?: (type: FilterType) => void
  actived?: boolean
}) => {
  const { type, count, onClick, actived } = props
  const { t } = useTranslation()

  const activedCss = actived
    ? css`
        border: 1px solid #20e4ff;
      `
    : css``

  return (
    <div
      onClick={() => {
        onClick && onClick(type)
      }}
      css={[
        css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          min-width: 56px;
          min-height: 48px;
          border-radius: 4px;
          background-color: rgba(255, 255, 255, 0.05);
          color: ${type === 'Today' ? '#51bcff' : '#e17567'};
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        `,
        activedCss,
      ]}>
      <div>
        {cond([
          [equals('Today'), always(t('dashboard:today'))],
          [equals('Checked'), always(t('dashboard:pending'))],
          [T, always('')],
        ])(type)}
      </div>
      <div>{count}</div>
    </div>
  )
}

const AlarmRow = (props: {
  alarm: Alarm
  highlight?: boolean
  onSuccess?: () => any
  handleVsDevices: (alarm: Alarm) => any
}) => {
  const { alarm, onSuccess, handleVsDevices, highlight } = props
  const { t } = useTranslation()

  const { handleCheck, isCheckNow } = useCheckAlarm()

  const { convertDetailPageUrl } = useConvertData()

  const [activeMarker] = useRecoilState(MapActiveMarkerState)

  const { handleActiveMarker, handleActiveMarkers } = useMap()

  const { refetch } = useDevice(
    {
      deviceType: alarm.deviceType,
      id: alarm.deviceSeq,
    },
    {
      enabled: false,
      onSuccess: (data: any) => {
        if (activeMarker !== data) {
          const _deviceData = {
            ...data,
            ...convertDetailPageUrl(data),
          }
          handleActiveMarker(_deviceData)
          handleActiveMarkers([_deviceData])
        }
      },
    }
  )

  return (
    <div
      className={cx({ 'camera-sidebar-trigger': true, highlight })}
      css={css`
        display: flex;
        align-items: center;
        width: auto;
        height: 32px;
        margin: 2px auto;
        background-color: rgba(255, 255, 255, 0.1);
        color: #ffffff;
        border-radius: 5px;
        font-size: 14px;
        &:nth-of-type(odd) {
          background-color: rgba(255, 255, 255, 0.05);
        }
        &:hover,
        &.highlight {
          background-color: rgba(255, 204, 43, 0.2);
          cursor: pointer;
        }
      `}
      onClick={() => {
        handleVsDevices(alarm)
      }}>
      <div
        css={css`
          width: 4px;
          height: 100%;
          background-color: ${alarm.checked ? '#a1ddff' : '#e17567'};
          margin-right: 8px;
          border-radius: 5px 0 0 5px;
        `}
      />
      <div
        className="truncate"
        css={css`
          min-width: 140px;
          font-size: 12px;
          width: 10%;
        `}>
        {momentToString(alarm.createAt)}
      </div>
      <div
        className="truncate"
        css={css`
          min-width: 220px;
          text-align: left;
          padding-left: 10px;
          width: 20%;
        `}>
        <label
          className="text-blue cursor-pointer"
          onClick={() => {
            refetch()
          }}>
          {alarm.displayName}
        </label>
      </div>
      <div
        className="truncate"
        css={css`
          min-width: 140px;
          text-align: left;
          padding-left: 10px;
          width: 10%;
        `}>
        {alarm.sessionName}
      </div>
      <div
        className="truncate"
        css={css`
          min-width: 140px;
          text-align: left;
          padding-left: 10px;
          width: 10%;
        `}>
        {alarm.alarmTypeDesc}
      </div>
      <div
        className="truncate"
        css={css`
          width: 340px;
          text-align: left;
          padding-left: 10px;
          width: 50%;
        `}>
        {alarm.alarmMessage}
      </div>
      <div
        className="truncate"
        css={css`
          min-width: 140px;
          padding-left: 10px;
          width: 10%;
          display: flex;
          justify-content: center;
        `}>
        {isCheckNow ? (
          <Spin spinning />
        ) : (
          <div
            css={css`
              margin: 0 auto;
              border: 1px solid #e17567;
              color: #e17567;
              display: flex;
              width: 48px;
              border-radius: 4px;
              justify-content: center;
              align-items: center;
              font-size: 12px;
              opacity: ${alarm.checked ? 0.3 : 1};
              cursor: ${alarm.checked ? 'default' : 'pointer'};
            `}
            onClick={() => {
              if (alarm.checked) {
                return
              }
              handleCheck(
                [
                  {
                    id: alarm.id,
                    check: true,
                  },
                ],
                {
                  onSuccess,
                }
              )
            }}>
            {alarm.checked
              ? t('dashboard:confirmed')
              : t('dashboard:pending_confirmation')}
          </div>
        )}
      </div>
    </div>
  )
}
